import { useEffect, useMemo, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import {
  evaluateSpPasswordRules,
  SP_PASSWORD_RULE_ORDER,
} from '@/features/auth/lib/password-rules'
import {
  getPasswordChangeErrorMessage,
  PasswordLockoutError,
  useChangePassword,
  useVerifyCurrentPassword,
} from '@/features/account/api/password-change-mutations'
import type { PasswordChangeFormData } from '@/features/account/lib/account-settings-schema'
import { useNotify } from '@/shared/toast/ToastProvider'
import { runDetached } from '@/shared/lib/runDetachedPromise'

function allAccountPasswordRulesMet(password: string): boolean {
  const checks = evaluateSpPasswordRules(password)
  return SP_PASSWORD_RULE_ORDER.every((key) => checks[key])
}

export function usePasswordChangeFlow(
  form: UseFormReturn<PasswordChangeFormData>,
) {
  const notify = useNotify()
  const verifyCurrent = useVerifyCurrentPassword()
  const changePassword = useChangePassword()
  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = form

  const [currentPassword, setCurrentPassword] = useState('')
  const [currentVerified, setCurrentVerified] = useState(false)
  const [currentError, setCurrentError] = useState<string | null>(null)
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null)
  const [lockoutTick, setLockoutTick] = useState(0)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const newPassword = watch('newPassword')
  const confirmPassword = watch('confirmPassword')

  const ruleChecks = useMemo(
    () => evaluateSpPasswordRules(newPassword ?? ''),
    [newPassword],
  )
  const rulesComplete = allAccountPasswordRulesMet(newPassword ?? '')
  const passwordsMatch =
    (newPassword?.length ?? 0) > 0 &&
    (confirmPassword?.length ?? 0) > 0 &&
    newPassword === confirmPassword

  const lockoutRemaining = lockoutUntil
    ? Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000))
    : 0
  const isLockedOut = lockoutRemaining > 0

  useEffect(() => {
    if (!lockoutUntil) return
    const id = globalThis.setInterval(() => setLockoutTick((t) => t + 1), 1000)
    return () => globalThis.clearInterval(id)
  }, [lockoutUntil, lockoutTick])

  useEffect(() => {
    if (lockoutUntil && lockoutRemaining <= 0) {
      setLockoutUntil(null)
      setCurrentError(null)
    }
  }, [lockoutUntil, lockoutRemaining])

  useEffect(() => {
    if (!currentVerified || (confirmPassword?.length ?? 0) === 0) return
    runDetached(trigger('confirmPassword'))
  }, [newPassword, currentVerified, confirmPassword, trigger])

  const confirmInlineError = (() => {
    if (!currentVerified) return null
    const confirmLen = confirmPassword?.length ?? 0
    if (confirmLen > 0) {
      return newPassword === confirmPassword ? null : 'Passwords do not match'
    }
    return errors.confirmPassword?.message ?? null
  })()

  const canVerifyCurrent =
    !isLockedOut &&
    !currentVerified &&
    currentPassword.trim().length > 0 &&
    !verifyCurrent.isPending

  const canSubmitChange =
    currentVerified &&
    !isLockedOut &&
    rulesComplete &&
    passwordsMatch &&
    !changePassword.isPending

  const resetPasswordFlow = () => {
    setCurrentPassword('')
    setCurrentVerified(false)
    if (!isLockedOut) setCurrentError(null)
    reset({ newPassword: '', confirmPassword: '' })
    setShowCurrent(false)
    setShowNew(false)
    setShowConfirm(false)
  }

  const handleVerifyCurrent = () => {
    setCurrentError(null)
    verifyCurrent.mutate(currentPassword, {
      onSuccess: () => {
        setCurrentVerified(true)
        setCurrentError(null)
        setShowCurrent(false)
      },
      onError: (error) => {
        if (error instanceof PasswordLockoutError) {
          setCurrentPassword('')
          setCurrentVerified(false)
          setShowCurrent(false)
          setLockoutUntil(Date.now() + error.retryAfterSeconds * 1000)
          setCurrentError(error.message)
          return
        }
        setCurrentError(
          getPasswordChangeErrorMessage(error, 'Current password is incorrect'),
        )
      },
    })
  }

  const onSubmitChange = (data: PasswordChangeFormData) => {
    changePassword.mutate(data, {
      onSuccess: () => {
        notify.showToast('Password updated successfully.', 'success')
        resetPasswordFlow()
      },
      onError: (error) => {
        notify.showToast(
          getPasswordChangeErrorMessage(
            error,
            'Something went wrong. Please try again.',
          ),
          'error',
        )
      },
    })
  }

  const handlePrimaryClick = () => {
    if (currentVerified) {
      runDetached(Promise.resolve(handleSubmit(onSubmitChange)()))
    } else {
      handleVerifyCurrent()
    }
  }

  const primaryDisabled = currentVerified ? !canSubmitChange : !canVerifyCurrent
  const primaryPending = currentVerified
    ? changePassword.isPending
    : verifyCurrent.isPending
  return {
    currentPassword,
    setCurrentPassword,
    currentVerified,
    currentError,
    isLockedOut,
    lockoutRemaining,
    showCurrent,
    setShowCurrent,
    showNew,
    setShowNew,
    showConfirm,
    setShowConfirm,
    newPassword,
    confirmPassword,
    setValue,
    ruleChecks,
    rulesComplete,
    confirmInlineError,
    primaryDisabled,
    primaryPending,
    handlePrimaryClick,
  }
}
